import type { CurrencyCode } from '@percent/utility';

interface LocalisedRequestParams {
  locale?: string;
}

export interface GetNominationFormParams extends LocalisedRequestParams {
  programId?: string;
}

export interface SubmitNominationFormParams extends LocalisedRequestParams {
  programId?: string;
  data: object;
}

export interface GetNominationFormResponse {
  data: NominationForm;
}

export enum EntryType {
  NOMINATION = 'nomination',
  APPLICATION = 'application',
  INVITATION = 'invitation',
}

export enum SupportedValidationRequestOrganisationTypes {
  Nonprofit = 'nonprofit',
  SocialImpact = 'social_impact',
  Education = 'education',
}

export interface NominationForm {
  programName: string;
  title?: string;
  description: string;
  deadline: Date;
  logo?: string;
  primaryColor: string;
  secondaryColor: string;
  postSubmitTitle: string;
  postSubmitDescription: string;
  pages: NominationFormPage[];
  isSupportedLocale: boolean;
  entryType: EntryType;
  shareNominatorInfoWithOrg: boolean;
  allowedOrganisationsTypes: SupportedValidationRequestOrganisationTypes[];
}

type BaseQuestion = {
  id: string;
  key: string;
  label: string;
  placeholder?: string;
  description?: string;
  instructions?: string;
  required: boolean;
  order: number;
  hidden?: boolean;
};

/**
 * Currency config authored per money field. The form-serving endpoint returns it alongside the raw amount and
 * currency questions; `fuseOperatingBudgetQuestions` lifts it onto the fused money/currency question, so only
 * those two types carry it here.
 */
export type CurrencyConfig = {
  defaultCurrency?: CurrencyCode;
  allowedCurrencies?: CurrencyCode[];
};

type BaseFormQuestion = BaseQuestion & {
  type: 'text' | 'email' | 'website' | 'country_search' | 'registry_search' | 'number' | 'date';
};

type TextAreaQuestion = BaseQuestion & {
  type: 'textarea';
  maxLength?: number;
};

export interface SelectOption {
  label: string;
  value: string;
  conditionalQuestionIds?: string[];
}

type SelectQuestion = BaseQuestion & {
  type: 'select';
  options: SelectOption[];
  multiple?: boolean;
};

export type OrganisationSearchQuestion = BaseQuestion & {
  type: 'organisation_search';
  addOrganisationQuestions?: string[];
};

export type MoneyQuestion = BaseQuestion &
  CurrencyConfig & {
    type: 'money';
    amountKey: string;
    currencyKey?: string;
    amountQuestionId: string;
    currencyQuestionId?: string;
    amountRequired: boolean;
  };

export type CurrencyOnlyQuestion = BaseQuestion &
  CurrencyConfig & {
    type: 'currency';
  };

export type FormQuestion =
  | BaseFormQuestion
  | TextAreaQuestion
  | SelectQuestion
  | OrganisationSearchQuestion
  | MoneyQuestion
  | CurrencyOnlyQuestion;

export interface NominationFormPage {
  id: string;
  title: string;
  description: string;
  order: number;
  questions?: FormQuestion[];
}
